import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn} from "@lib/apis";
import {SyntheticEvent, useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {Box, Button, Tab, Tabs, Grid, Card, CardContent, Typography, FormControl, Select, InputLabel} from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import {
    DialogContentText,
    DialogTitle,
    Divider,
    Stack,
    TextField,  
    Menu,
	MenuItem,  
} from "@mui/material";
import {AddCircleOutline} from "@mui/icons-material";
import {useDialogs} from "@toolpad/core";
import { ReactComponent as HubspotLogo } from "@assets/images/hubspot.svg";
import { ReactComponent as GoogleSheetsLogo } from "@assets/images/googleSheets.svg";
import { ReactComponent as SalesforceLogo } from "@assets/images/salesforce.svg";
import { ReactComponent as SmartleadLogo } from "@assets/images/smartlead.svg";
import { ReactComponent as InstantlyLogo } from "@assets/images/instantly.svg";
import { ReactComponent as DeliverymanLogo } from "@assets/images/coldemailer_icon_logo.svg";
import { ReactComponent as Pipedrive } from "@assets/images/pipedrive.svg";
import { ReactComponent as Spaceship } from "@assets/images/spaceship.svg";
import { ReactComponent as Godaddy } from "@assets/images/GoDaddy-Logo.wine.svg";


const integrations = [
  { name: "Hubspot", Logo: HubspotLogo },
  { name: "Google Sheets", Logo: GoogleSheetsLogo },
  { name: "Salesforce", Logo: SalesforceLogo },
  { name: "Instantly", Logo: InstantlyLogo },
  { name: "Smartlead", Logo: SmartleadLogo },
  { name: "Deliveryman API Keys", Logo: DeliverymanLogo },
  { name: "Pipedrive", Logo: Pipedrive},
  { name: "Spaceship", Logo: Spaceship},
  { name: "Godaddy", Logo: Godaddy}
];

const states = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
  { value: "maintenance", label: "Under Maintenance" },
  { value: "coming_soon", label: "Coming Soon" },
];

interface Integrations{
    id: string;
    name: string;
    state: string;
    updated_at: string;
}

export default function AdminIntegrations() {        

    const [tabValue, setTabValue] = useState<number>(0);
    const [openAddIntegration, setOpenAddIntegration] = useState(false)
    const [pageData , setPageData] = useState<Integrations[]>([])

    const getAllIntegrationPageDataQuery = useQuery({
            queryKey: ["getAllIntegrationPageData"],
            queryFn: () => authenticateAndFetchData("/admin/get-all-integrations/"),
            gcTime: 0,
            retry: retryFn,
            refetchOnWindowFocus: false,
        })
        
    useEffect(() => {
        if (getAllIntegrationPageDataQuery.data) {            
            setPageData(getAllIntegrationPageDataQuery.data.data.data);
        }
    }, [getAllIntegrationPageDataQuery.data]);

    const handleTabChange = async (event: SyntheticEvent, newValue: number) => {            
            setTabValue(newValue);
        };
    
    const filteredIntegrations = pageData.filter(integration => {
        if (tabValue === 0) return integration.state === "enabled";
        if (tabValue === 1) return integration.state === "disabled";
        if (tabValue === 2) return integration.state === "maintenance";
        if (tabValue === 3) return integration.state === "coming_soon";
        return true;
    });

    return (
        <Stack direction={"column"} spacing={2} sx={{pb: 4, height: "100%"}}>
        <Typography fontWeight={"bold"} variant={"h5"}>Integrations</Typography>
        <Divider/>
        <Stack direction={"column"} spacing={2} sx={{height: "100%"}}>
        <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
            <Stack direction={"row"} spacing={2}>
                <Button variant={"contained"}
                                size={"small"}
                                startIcon={<AddCircleOutline/>}
                                disabled={false}
                                onClick={() => {
                                    setOpenAddIntegration(true)                                    
                                }}>
                    Configure Integration
                </Button>               
            </Stack>
        </Box>
        <Box sx={{borderBottom: 1, borderColor: 'divider', mt: 1}}>            
            <Tabs value={tabValue} onChange={handleTabChange} sx={{
                '& .MuiTabs-flexContainer': {
                    flexWrap: 'wrap',
                },
            }}>
                <Tab label="Enabled" />
                <Tab label="Disabled" />
                <Tab label="Under Maintenance" />
                <Tab label="Coming Soon" />
            </Tabs>
        </Box>
        <Grid container spacing={3} justifyContent="center" sx={{ px: 2, mt: 2 }}>
        {filteredIntegrations.length > 0 ? (
            filteredIntegrations.map((integration) => {            
            const matchedIntegration = integrations.find((i) => i.name === integration.name);
            const Logo = matchedIntegration ? matchedIntegration.Logo : null;

            return (
                <Grid item xs={12} sm={6} md={3} key={integration.id}>
                <Card
                    sx={{
                    textAlign: "center",
                    p: 3,
                    borderRadius: 3,
                    boxShadow: 3,
                    height: "100%",
                    }}
                >
                    <CardContent>
                    <Box
                        sx={{
                        height: 60,
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        }}
                    >
                        {Logo && (
                        <Logo
                            style={{
                            height: "100%",
                            maxHeight: "60px",
                            width: "auto",
                            }}
                        />
                        )}
                    </Box>

                    <Typography variant="h6" fontWeight="bold">
                        {integration.name}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                    >
                        {states.find((s) => s.value === integration.state)?.label ??
                        integration.state}
                    </Typography>
                    </CardContent>
                </Card>
                </Grid>
            );
            })
        ) : (
            <Grid item xs={12}>
            <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                sx={{ mt: 4 }}
            >
                No integrations available in this category.
            </Typography>
            </Grid>
        )}
        </Grid>


        </Stack>
        <AddIntegration open={openAddIntegration}   
                pageData={pageData}             
                onClose={() => setOpenAddIntegration(false)}
                onSuccess={() => {
                    setOpenAddIntegration(false);
                    getAllIntegrationPageDataQuery.refetch().then();                    
                }}/>		
    </Stack>
    )
}


function AddIntegration(props: {
    open: boolean;
    pageData: Integrations[];
    onClose: () => void;
    onSuccess: () => void;
    }) {
    const { enqueueSnackbar } = useSnackbar();
    const [integrationName, setIntegrationName] = useState("");
    const [state, setState] = useState("");        

    const existingIntegration = props.pageData.find(
        (i) => i.name === integrationName
    );
    const isUpdate = !!existingIntegration;

    const addIntegrationMutation = useMutation({
		mutationKey: ["addIntegration"],
		mutationFn: () => authenticateAndPostData("/admin/add-update-integration/", {
			integrationName: integrationName,
            state: state,
		}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {            
            enqueueSnackbar(`Added ${integrationName} (${state})`, { variant: "success" });
            props.onSuccess()
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, {
				variant: "error",
			});
		}
	});

    const handleSubmit = () => {
        addIntegrationMutation.mutate()                
    };

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add or Update Integration</DialogTitle>
        <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>Integration Name:</DialogContentText>
            <FormControl fullWidth>
            <InputLabel>Integration</InputLabel>
            <Select
                value={integrationName}
                onChange={(e) => {
                setIntegrationName(e.target.value);
                // if integration exists, prefill state
                const existing = props.pageData.find((i) => i.name === e.target.value);
                setState(existing ? existing.state : "");
                }}
                label="Integration"
            >
                {integrations.map((int) => (
                <MenuItem key={int.name} value={int.name}>
                    <int.Logo
                    style={{ width: 20, height: 20, marginRight: 8 }}
                    />
                    {int.name}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            <DialogContentText sx={{ mt: 3, mb: 1 }}>State:</DialogContentText>
            <FormControl fullWidth>
            <InputLabel>State</InputLabel>
            <Select
                value={state}
                onChange={(e) => setState(e.target.value)}
                label="State"
            >
                {states.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                    {s.label}
                </MenuItem>
                ))}
            </Select>
            </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!integrationName || !state}
            >
            {isUpdate ? "Update Integration" : "Add Integration"}
            </Button>
        </DialogActions>
        </Dialog>
    );
    }
